import React, { useMemo, useState, useCallback, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions, ApexAxisChartSeries } from "apexcharts";
import { fixMarkerOverlaps } from "./graphUtils";

// ── Types ─────────────────────────────────────────────────────────────────────
export interface StreakGame {
  game_date: string;
  opponent: string;
  opponent_code: string | null;
  stat_value: number;
}

export interface StreakPlayer {
  player_id: string;
  player_name: string;
  team_name: string;
  streak_length: number;
  streak_start_date: string;
  streak_end_date: string;
  games: StreakGame[];
}

export interface StreakGraphData {
  query_type: string;
  chart_type: string;
  sql_query?: string;
  labels: string[];
  values: number[];
  players: StreakPlayer[];
  total_records: number;
}

interface StreakGraphProps {
  data: StreakGraphData;
  chartReady?: boolean;
  statLabel?: string;
}

// ── Palette ───────────────────────────────────────────────────────────────────
const PALETTE = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#EC4899",
  "#8B5CF6",
  "#06B6D4",
  "#F97316",
  "#14B8A6",
  "#EF4444",
  "#84CC16",
];



// ── Helpers ───────────────────────────────────────────────────────────────────
function formatDateShort(dateStr: string): string {
  if (/^\d{4}$/.test(dateStr.trim())) return dateStr; // season year
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = String(d.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
}

function formatDateFull(dateStr: string): string {
  if (/^\d{4}$/.test(dateStr.trim())) return dateStr; // season year
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = d.toLocaleDateString("en-US", { month: "short" });
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function sortedGames(player: StreakPlayer): StreakGame[] {
  return [...player.games].sort(
    (a, b) => new Date(a.game_date).getTime() - new Date(b.game_date).getTime()
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&family=JetBrains+Mono:wght@400;500;600&display=swap');

  .skg * { box-sizing: border-box; }

  .skg {
    --bg: #ffffff;
    --surface: #f8fafc;
    --border: #e8edf3;
    --border-accent: #cbd5e1;
    --text-primary: #0f172a;
    --text-secondary: #475569;
    --text-muted: #94a3b8;
    --accent: #10B981;
    --accent-light: #ecfdf5;
    --accent-border: #6ee7b7;
    --accent-text: #065f46;
    font-family: 'Plus Jakarta Sans', sans-serif;
    background: var(--bg);
    border-radius: 24px;
    padding: 24px 28px;
    position: relative;
    width: 100%;
    border: 1px solid var(--border);
    box-shadow:
      0 1px 2px rgba(0,0,0,0.04),
      0 4px 16px rgba(0,0,0,0.06),
      0 16px 48px rgba(0,0,0,0.04);
  }

  .skg::before {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 260px; height: 160px;
    background: radial-gradient(ellipse at top right, rgba(16,185,129,0.07) 0%, transparent 65%);
    pointer-events: none;
    border-radius: 0 24px 0 0;
  }

  .skg::after {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle, rgba(148,163,184,0.18) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
    border-radius: 24px;
    opacity: 0.55;
    z-index: 0;
  }

  .skg-chips-wrapper {
    position: relative;
    z-index: 1;
    margin-bottom: 18px;
  }

  .skg-chips {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    padding: 12px 14px;
    background: var(--surface);
    border-radius: 14px;
    border: 1px solid var(--border); max-height: 164px; overflow-y: auto;
  }

  .skg-chip-all {
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 6px 13px;
    border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px;
    font-weight: 700;
    border: 1.5px solid var(--border-accent);
    background: white;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.18s ease;
    white-space: nowrap;
  }

  .skg-chip-all:hover {
    background: var(--accent-light);
    border-color: var(--accent-border);
    color: var(--accent-text);
  }

  .skg-chip-all.active {
    background: var(--accent-light);
    border-color: var(--accent-border);
    color: var(--accent-text);
  }

  .skg-chip {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 5px 10px 5px 8px;
    border-radius: 9px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 11px;
    font-weight: 600;
    border: 1.5px solid transparent;
    background: white;
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.18s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
    height: 34px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  }

  .skg-chip:hover {
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    color: var(--text-primary);
  }

  .skg-chip.active {
    border-color: var(--chip-border);
    background: var(--chip-bg);
    color: var(--chip-color);
    box-shadow: 0 2px 8px var(--chip-shadow);
  }

  .skg-swatch {
    width: 7px; height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }

  .skg-chip.active .skg-swatch {
    transform: scale(1.4);
    box-shadow: 0 0 6px var(--chip-color);
  }

  .skg-chip-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 110px;
  }

  .skg-chip-stat-badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    background: rgba(0,0,0,0.06);
    color: var(--text-secondary);
    flex-shrink: 0;
    transition: background 0.18s ease, color 0.18s ease;
  }

  .skg-chip.active .skg-chip-stat-badge {
    background: var(--chip-color);
    color: #ffffff;
  }

  .skg-chip-check {
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .skg-chart-outer {
    position: relative;
    z-index: 2;
  }

  .skg-chart-wrapper {
    position: relative;
    width: 100%;
    border-radius: 14px;
    overflow-x: auto;
    overflow-y: hidden;
    background: white; padding-top: 26px;
    padding-top:50px;
    border: 1px solid var(--border);
  }

  .skg-chart-wrapper .apexcharts-tooltip {
    z-index: 9999 !important;
    overflow: visible !important;
  }

  .skg-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--text-muted);
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    gap: 8px;
  }

  .skg-empty-icon { font-size: 28px; opacity: 0.35; }
`;

// ── Component ──────────────────────────────────────────────────────────────────
const StreakGraph: React.FC<StreakGraphProps> = ({
  data,
  chartReady = true,
  statLabel = "Stat Value",
}) => {
  const { players } = data;

  const [selectedPlayers, setSelectedPlayers] = useState<Set<number>>(
    () => new Set(Array.from({ length: Math.min(2, players.length) }, (_, i) => i))
  );

  const togglePlayer = useCallback((idx: number) => {
    setSelectedPlayers(prev => {
      const next = new Set(prev);
      if (next.has(idx)) { next.delete(idx); } else if (next.size >= 2) { return prev; } else { next.add(idx); }
      return next;
    });
  }, []);

  const selectAllPlayers = useCallback(() => {
    if (selectedPlayers.size >= Math.min(2, players.length)) {
      setSelectedPlayers(new Set([0]));
    } else {
      setSelectedPlayers(new Set(Array.from({ length: Math.min(2, players.length) }, (_, i) => i)));
    }
  }, [players.length, selectedPlayers.size]);

  // Only keep indices that actually exist in the current players array.
  // This prevents crashes when selectedPlayers holds stale indices from a
  // previous render whose data had more players than the current one.
  const selectedIndicesOrdered = useMemo(
    () =>
      Array.from(selectedPlayers)
        .filter(idx => idx >= 0 && idx < players.length)
        .sort((a, b) => a - b),
    [selectedPlayers, players.length]
  );

  const selectedCount = selectedIndicesOrdered.length;

  // Assign unique colors to selected players (dedupe if same slot)
  const playerColorMap = useMemo(() => {
    const map = new Map<number, string>();
    const used = new Set<number>();
    selectedIndicesOrdered.forEach((playerIdx) => {
      let slot = playerIdx % PALETTE.length;
      while (used.has(slot)) slot = (slot + 1) % PALETTE.length;
      used.add(slot);
      map.set(playerIdx, PALETTE[slot]);
    });
    return map;
  }, [selectedIndicesOrdered]);

  const maxGames = useMemo(() => {
    let max = 0;
    selectedIndicesOrdered.forEach(idx => {
      const len = players[idx]?.games.length ?? 0;
      if (len > max) max = len;
    });
    return max;
  }, [players, selectedIndicesOrdered]);

  // ── FIXED: x-axis categories ───────────────────────────────────────────────
  // Single player → actual dates. Multi-player → G1..Gn based on maxGames only.
  const xCategories = useMemo(() => {
    if (selectedCount === 1) {
      const player = players[selectedIndicesOrdered[0]];
      if (!player) return [];
      const games = sortedGames(player);
      return games.map(g => formatDateShort(g.game_date));
    }
    return Array.from({ length: maxGames }, (_, i) => `G${i + 1}`);
  }, [selectedCount, selectedIndicesOrdered, players, maxGames]);

  const chartWidth = Math.max(700, xCategories.length * 90);

  // ── FIXED: series uses {x, y} objects so each player pins to exact slots ──
  // This prevents ApexCharts from distributing a shorter series across all
  // category slots, which was causing ghost/phantom data points.
  const series: ApexAxisChartSeries = useMemo(() => {
    return selectedIndicesOrdered
      .map((playerIdx) => {
        const player = players[playerIdx];
        if (!player) return null;
        const games = sortedGames(player);

        if (selectedCount === 1) {
          // Single player: plain values are fine since categories === game count
          return {
            name: player.player_name,
            data: games.map(g => g.stat_value),
          };
        }

        // Multi-player: use {x, y} with explicit category label so each point
        // is pinned to its own G1/G2/... slot — no spill into other slots.
        return {
          name: player.player_name,
          data: games.map((g, i) => ({
            x: `G${i + 1}`,   // must match exactly what's in xCategories
            y: g.stat_value,
          })),
        };
      })
      .filter(Boolean) as ApexAxisChartSeries;
  }, [players, selectedIndicesOrdered, selectedCount]);

  const chartHeight = 370;

  const chartWrapperRef = useRef<HTMLDivElement>(null);

  const options: ApexOptions = useMemo(() => {
    const isSinglePlayer = selectedCount === 1;
    const labelColor = isSinglePlayer
      ? PALETTE[selectedIndicesOrdered[0] % PALETTE.length]
      : "#475569";

    return {
      chart: {
        type: "line",
        background: "transparent",
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        animations: { enabled: true, speed: 650, easing: "easeinout" },
        height: chartHeight,
        events: { mounted: (c: any) => fixMarkerOverlaps(c.el), updated: (c: any) => fixMarkerOverlaps(c.el) },
      },
      colors: selectedIndicesOrdered.map(i => PALETTE[i % PALETTE.length]),
      stroke: { curve: "smooth", width: 2.5, lineCap: "round" },
      markers: {
          size: 5,
          strokeWidth: 2.5,
          strokeColors: "#ffffff",
          strokeOpacity: 1,
          hover: { size: 6 },
          onClick: (e) => {
            const wrapper = chartWrapperRef.current;
            if (!wrapper) return;

            const point = e.target;
            const pointX = point.getBoundingClientRect().left;
            const wrapperX = wrapper.getBoundingClientRect().left;

            const offset = pointX - wrapperX;

            wrapper.scrollTo({
              left: wrapper.scrollLeft + offset - wrapper.clientWidth / 2,
              behavior: "smooth"
            });
          }
        },
      xaxis: {
        type: "category",
        categories: xCategories,
        tickPlacement: "on",
        labels: {
            show: true,
            rotate: -35,
            rotateAlways: false,
            hideOverlappingLabels: true,
            trim: false,
            style: {
              colors: labelColor,
              fontSize: "10px",
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: "200",
            },
        },
        axisBorder: { show: true, color: "#e8edf3", height: 1 },
        axisTicks: { show: false },
        title: {
          text: isSinglePlayer ? "Game date" : "Game # in streak",
          style: {
            color: "#94a3b8",
            fontSize: "10px",
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: "500",
          },
          offsetY: 4,
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#94a3b8",
            fontSize: "10px",
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: "500",
          },
          formatter: (val: number) => val.toFixed(0),
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
        title: {
          text: statLabel,
          style: {
            color: "#94a3b8",
            fontSize: "10px",
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: "500",
          },
          offsetX: -4,
        },
      },
      grid: {
        show: true,
        borderColor: "#f1f5f9",
        strokeDashArray: 3,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 10, right: 20, bottom: 20, left: 0 },
      },
      legend: { show: false },
      tooltip: {
      theme: "light",
        shared: false,
        intersect: true,
        followCursor: true,
        fixed: {
          enabled: false
        },       
         custom: ({ seriesIndex, dataPointIndex }) => {
          if (seriesIndex >= selectedIndicesOrdered.length) return "";
          const playerIdx = selectedIndicesOrdered[seriesIndex];
          const player = players[playerIdx];
          if (!player) return "";
          const games = sortedGames(player);
          const game = games[dataPointIndex];
          if (!game) return "";
          const color = PALETTE[playerIdx % PALETTE.length];
          const gameNum = dataPointIndex + 1;

          return `
            <div style="background:#ffffff;border:1px solid #e2e8f0;border-top:3px solid ${color};border-radius:14px;padding:10px 15px;font-family:'Plus Jakarta Sans',sans-serif;box-shadow:0 8px 30px rgba(0,0,0,0.12);min-width:185px;">
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
                <div style="width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></div>
                <span style="font-weight:700;color:#0f172a;font-size:13px;flex:1;">${player.player_name}</span>
                <span style="font-family:'JetBrains Mono',monospace;font-size:10px;font-weight:600;color:${color};background:${color}15;padding:2px 8px;border-radius:6px;">G${gameNum}</span>
              </div>
              <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:10px;">
                <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
                  <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;margin-bottom:3px;">Date</div>
                  <div style="font-size:11px;font-weight:600;">${formatDateFull(game.game_date)}</div>
                </div>
                <div style="background:#f8fafc;border-radius:8px;padding:7px 9px;border:1px solid #f1f5f9;">
                  <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#94a3b8;text-transform:uppercase;margin-bottom:3px;">vs</div>
                  <div style="font-size:11px;font-weight:600;">${game.opponent || "N/A"}</div>
                </div>
              </div>
              <div style="background:#ecfdf5;border-radius:8px;padding:5px 9px;border:1px solid #6ee7b7;margin-bottom:8px;">
                <div style="font-family:'JetBrains Mono',monospace;font-size:8px;color:#065f46;text-transform:uppercase;margin-bottom:1px;">Streak</div>
                <div style="font-size:11px;font-weight:700;color:#065f46;">${gameNum} of ${player.streak_length} games</div>
              </div>
              <div style="display:flex;align-items:center;justify-content:space-between;background:${color}10;border:1px solid ${color}25;border-radius:10px;padding:1px 13px;">
                <span style="font-family:'JetBrains Mono',monospace;font-size:9px;color:#64748b;text-transform:uppercase;">${statLabel}</span>
                <span style="font-size:21px;font-weight:800;color:${color};">${game.stat_value}</span>
              </div>
            </div>
          `;
        },
      },
      dataLabels: { enabled: false },
    };
  }, [players, statLabel, selectedIndicesOrdered, selectedCount, xCategories, chartHeight]);

  if (!players || players.length === 0) {
    return (
      <>
        <style>{css}</style>
        <div className="skg">
          <div className="skg-empty" style={{ height: 360 }}>
            <span className="skg-empty-icon">📭</span>
            <span>No streak data available</span>
          </div>
        </div>
      </>
    );
  }

  const allSelected = selectedPlayers.size === Math.min(2, players.length);

  return (
    <>
      <style>{css}</style>
      <div className="skg">
        {selectedPlayers.size >= 2 && (
  <div style={{ position:"relative", zIndex:2, marginBottom:8, fontSize:11, color:"#b45309", fontWeight:600, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:6, padding:"4px 12px", textAlign:"center" }}>
    Maximum 2 players can be compared at once. Deselect a player to add another.
  </div>
)}

        <div className="skg-chips-wrapper">
          <div className="skg-chips">
            <button
              className={`skg-chip-all${allSelected ? " active" : ""}`}
              onClick={selectAllPlayers}
            >
              {allSelected ? "✕ Clear" : `⊕ All (up to ${Math.min(2, players.length)})`}
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "10px" }}>
                ({players.length})
              </span>
            </button>

            {players.map((player, idx) => {
              const active = selectedPlayers.has(idx);
              const color = playerColorMap.get(idx) ?? "#cbd5e1";
              return (
                <button
                  key={idx}
                  className={`skg-chip${active ? " active" : ""}`}
                  onClick={() => togglePlayer(idx)}
                  style={{
                    ["--chip-color" as any]: color,
                    ["--chip-bg" as any]: `${color}12`,
                    ["--chip-border" as any]: `${color}40`,
                    ["--chip-shadow" as any]: `${color}18`,
                  }}
                >
                  <span className="skg-swatch" style={{ background: active ? color : "#cbd5e1" }} />
                  <span className="skg-chip-name">{player.player_name}</span>
                  <span className="skg-chip-stat-badge">{player.streak_length}g</span>
                  {active && <span className="skg-chip-check" style={{ color }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="skg-chart-outer">
          <div className="skg-chart-wrapper" ref={chartWrapperRef}>
            {series.length > 0 && chartReady ? (
              <ReactApexChart
                type="line"
                series={series}
                options={options}
                height={chartHeight}
                width={chartWidth}
              />          
            ) : (
              <div className="skg-empty" style={{ height: chartHeight }}>
                <span className="skg-empty-icon">↑</span>
                <span>Select players above to view streaks</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default StreakGraph;